import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  BookOpen, 
  Shield, 
  Award, 
  Clock, 
  Users, 
  CheckCircle,
  ChevronRight,
  FileText,
  Bell,
  Sprout,
  Upload,
  ClipboardCheck,
  UserPlus,
  Mail,
  FileSearch,
  Lock,
  AlertTriangle,
  ArrowRight,
  Fingerprint,
  ShieldCheck,
  Table2,
  CalendarClock,
  Calendar,
  DollarSign,
  Repeat,
  UserCheck,
  BarChart3,
  Receipt,
  Zap,
  MousePointerClick
} from 'lucide-react';

const WelcomePage = () => {
  const platformPillars = [
    {
      icon: ClipboardCheck,
      title: "HR Onboarding",
      subtitle: "Safer Recruitment",
      description: "12-section digital onboarding with document uploads, identity verification, DBS checks, references, and safeguarding declarations.",
      highlights: ["Document uploads & verification", "Digital signatures", "Admin approve/reject workflow"]
    },
    {
      icon: BookOpen,
      title: "Staff Training",
      subtitle: "12 Mandatory Modules",
      description: "Comprehensive training covering Safeguarding, GDPR, Health & Safety, Prevent Duty, and more — with quizzes and PDF certificates.",
      highlights: ["Interactive quizzes (80% pass)", "PDF certificates", "12-month renewal cycle"]
    },
    {
      icon: Calendar,
      title: "Scheduling & Cover",
      subtitle: "Weekly Hour Management",
      description: "Admin assigns weekly sessions, tutors confirm by deadline. Unconfirmed hours auto-release to standby tutors with one-click email confirmation.",
      highlights: ["Tutor scheduling & confirmation", "Standby cover system", "One-click email confirm"]
    },
    {
      icon: DollarSign,
      title: "Timesheets & Pay",
      subtitle: "Automated Payroll",
      description: "Auto-generated timesheets, PDF invoices, and payroll tracking. Monthly automation locks sheets and generates invoices on schedule.",
      highlights: ["Auto-generate timesheets & invoices", "PDF invoice download", "Payment tracking dashboard"]
    }
  ];

  const features = [
    {
      icon: UserPlus,
      title: "Staff Self-Registration",
      description: "New staff register online and await admin approval before gaining access."
    },
    {
      icon: Upload,
      title: "Secure Document Uploads",
      description: "Upload passports, DBS certificates, qualifications, and references — stored securely."
    },
    {
      icon: ShieldCheck,
      title: "Admin Review & Approval",
      description: "Admins review each onboarding section, approve or return with feedback — full audit trail."
    },
    {
      icon: Award,
      title: "PDF Certificates",
      description: "Professional certificates on quiz completion, valid for 12 months with auto renewal tracking."
    },
    {
      icon: Calendar,
      title: "Weekly Scheduling",
      description: "Admin assigns tutoring sessions per week. Tutors confirm or decline by deadline."
    },
    {
      icon: Repeat,
      title: "Standby Cover",
      description: "Released or declined hours become visible to standby tutors who can request to cover them."
    },
    {
      icon: Receipt,
      title: "Automated Invoices",
      description: "Monthly timesheets auto-lock and PDF invoices are generated — ready for download and payment."
    },
    {
      icon: BarChart3,
      title: "Reliability Tracking",
      description: "Tutor reliability scores based on confirmed vs missed sessions — helping identify dependable staff."
    },
    {
      icon: MousePointerClick,
      title: "One-Click Email Confirm",
      description: "Tutors confirm sessions directly from their email — no login required."
    },
    {
      icon: Bell,
      title: "Automated Email Alerts",
      description: "Reminders for training, DBS/RTW expiry, onboarding, schedule deadlines, and invoice generation."
    },
    {
      icon: Table2,
      title: "Single Central Record",
      description: "Inspection-ready SCR with every staff member's safeguarding checks, printable for Ofsted."
    },
    {
      icon: Zap,
      title: "Compliance Gatekeeper",
      description: "Tutors cannot be scheduled until onboarding is approved, contract is signed, and rate is set."
    }
  ];

  const onboardingSections = [
    "Personal Details", "Identity Verification", "Right to Work", "DBS Check",
    "References", "Employment History", "Education & Qualifications", "Safeguarding Declaration",
    "Criminal Self-Declaration", "Health Declaration", "Policies & Agreements", "Bank Details"
  ];

  const trainingModules = [
    "Safeguarding (KCSIE)", "GDPR & Data Protection", "Health and Safety", "Manual Handling",
    "Basic First Aid", "Equality, Diversity & Inclusion", "Prevent Duty", "Online Safety",
    "Professional Conduct", "SEND Awareness", "Mental Health Awareness", "Fire Safety"
  ];

  const steps = [
    {
      num: "1",
      title: "Register & Get Approved",
      description: "Staff self-register online. An admin reviews and approves access to the platform.",
      icon: UserPlus
    },
    {
      num: "2",
      title: "Complete Onboarding",
      description: "Submit identity documents, DBS details, references, and declarations across 12 sections.",
      icon: ClipboardCheck
    },
    {
      num: "3",
      title: "Sign Contract & Train",
      description: "Sign your tutor contract digitally, then complete 12 mandatory training modules with quizzes.",
      icon: BookOpen
    },
    {
      num: "4",
      title: "Get Scheduled & Paid",
      description: "Receive weekly schedules, confirm sessions, and get automated timesheets and invoice PDFs.",
      icon: DollarSign
    }
  ];

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-gray-200" data-testid="landing-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-9 h-9 sm:w-11 sm:h-11 bg-[#0A0A0A] rounded-xl flex items-center justify-center">
                <Sprout className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div>
                <h1 className="text-sm sm:text-lg font-bold text-[#111827] tracking-tight">Growing Minds</h1>
                <p className="text-[10px] sm:text-xs text-[#4B5563] hidden sm:block">Staff Training, Scheduling & Compliance</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/register">
                <Button variant="outline" className="text-xs sm:text-sm border-gray-200 text-[#111827] hover:bg-gray-50" size="sm" data-testid="header-register-button">
                  Register
                </Button>
              </Link>
              <Link to="/login">
                <Button className="bg-[#002FA7] hover:bg-[#002482] text-white text-xs sm:text-sm" size="sm" data-testid="login-button">
                  Staff Login
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-[#0A0A0A] text-white py-16 sm:py-24 relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-[0.03]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#002FA7]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 left-10 w-56 h-56 bg-[#10B981]/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-6">
              <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" />
              <span className="text-xs text-white/80 tracking-wide uppercase">Safer Recruitment Compliant</span>
            </div>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-5 sm:mb-6 tracking-tight leading-[1.1]" data-testid="hero-title">
              Onboarding, Training,<br />
              Scheduling &<br />
              <span className="text-[#10B981]">Payroll</span> — Sorted.
            </h2>
            <p className="text-base sm:text-lg text-white/70 mb-8 max-w-xl leading-relaxed">
              The complete HR platform for Growing Minds Tuition. Digital onboarding, mandatory training, weekly scheduling, automated timesheets, invoice generation, and inspection-ready compliance records.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/login">
                <Button size="lg" className="bg-[#002FA7] hover:bg-[#002482] text-white w-full sm:w-auto text-sm sm:text-base" data-testid="hero-login-button">
                  <Users className="w-4 h-4 mr-2" />
                  Staff Login
                </Button>
              </Link>
              <Link to="/register">
                <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10 w-full sm:w-auto text-sm sm:text-base" data-testid="hero-register-button">
                  New Staff? Register Here
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-14 sm:mt-20">
            {[
              { value: "12", label: "Onboarding Sections" },
              { value: "12", label: "Training Modules" },
              { value: "Auto", label: "Timesheets & Invoices" },
              { value: "100%", label: "Compliance Tracking" }
            ].map((stat, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-3 sm:p-4 text-center" data-testid={`hero-stat-${i}`}>
                <div className="text-xl sm:text-3xl font-bold tracking-tight">{stat.value}</div>
                <div className="text-[10px] sm:text-xs text-white/50 mt-1 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-14 sm:py-24 bg-white border-b border-gray-100" data-testid="pillars-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#002FA7] font-medium mb-3">Platform Overview</p>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#111827] tracking-tight">
              Four Pillars of Your Platform
            </h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {platformPillars.map((pillar, index) => (
              <Card key={index} className="border border-black/10 hover:shadow-md hover:border-black/20 transition-all duration-200 rounded-lg" data-testid={`pillar-card-${index}`}>
                <CardContent className="p-5 sm:p-8">
                  <div className="w-11 h-11 sm:w-14 sm:h-14 bg-[#0A0A0A] rounded-xl flex items-center justify-center mb-4 sm:mb-5">
                    <pillar.icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                  </div>
                  <p className="text-xs uppercase tracking-[0.15em] text-[#002FA7] font-medium mb-1">{pillar.subtitle}</p>
                  <h4 className="font-bold text-lg sm:text-xl text-[#111827] mb-2 sm:mb-3 tracking-tight">{pillar.title}</h4>
                  <p className="text-sm text-[#4B5563] mb-4 leading-relaxed">{pillar.description}</p>
                  <div className="space-y-2">
                    {pillar.highlights.map((h, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="w-3.5 h-3.5 text-[#10B981] flex-shrink-0" />
                        <span className="text-xs sm:text-sm text-[#111827]">{h}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-14 sm:py-24 bg-[#F9FAFB]" data-testid="how-it-works-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#002FA7] font-medium mb-3">Process</p>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#111827] tracking-tight">
              From Registration to Compliance
            </h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-4">
            {steps.map((step, index) => (
              <div key={index} className="relative" data-testid={`step-${index}`}>
                <div className="text-center">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-[#0A0A0A] text-white rounded-2xl flex items-center justify-center mx-auto mb-4 relative">
                    <step.icon className="w-6 h-6 sm:w-7 sm:h-7" />
                    <span className="absolute -top-2 -right-2 w-6 h-6 bg-[#002FA7] text-white text-xs font-bold rounded-full flex items-center justify-center">{step.num}</span>
                  </div>
                  <h4 className="font-semibold text-base text-[#111827] mb-2 tracking-tight">{step.title}</h4>
                  <p className="text-xs sm:text-sm text-[#4B5563] leading-relaxed">{step.description}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)]">
                    <div className="border-t-2 border-dashed border-gray-300 w-full"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-14 sm:py-24 bg-white border-t border-gray-100" data-testid="features-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <p className="text-xs uppercase tracking-[0.2em] text-[#002FA7] font-medium mb-3">Features</p>
            <h3 className="text-2xl sm:text-3xl font-bold text-[#111827] tracking-tight">
              Built for Education Compliance
            </h3>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
            {features.map((feature, index) => (
              <div key={index} className="flex gap-3 sm:gap-4 p-4 sm:p-5 rounded-lg border border-black/5 hover:border-black/15 hover:bg-gray-50/50 transition-all duration-200" data-testid={`feature-${index}`}>
                <div className="w-9 h-9 sm:w-10 sm:h-10 bg-[#F9FAFB] border border-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#111827]" />
                </div>
                <div>
                  <h4 className="font-semibold text-sm sm:text-base text-[#111827] mb-1 tracking-tight">{feature.title}</h4>
                  <p className="text-xs sm:text-sm text-[#4B5563] leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Onboarding + Training Sections Side by Side */}
      <section className="py-14 sm:py-24 bg-[#F9FAFB] border-t border-gray-100" data-testid="checklist-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Onboarding */}
            <div>
              <div className="mb-5 sm:mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <ClipboardCheck className="w-5 h-5 text-[#002FA7]" />
                  <p className="text-xs uppercase tracking-[0.15em] text-[#002FA7] font-medium">Onboarding</p>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#111827] tracking-tight">12 Onboarding Sections</h3>
                <p className="text-sm text-[#4B5563] mt-1">Every section must be submitted and approved before training begins.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {onboardingSections.map((section, index) => (
                  <div key={index} className="flex items-center gap-2.5 p-2.5 sm:p-3 bg-white rounded-lg border border-black/5" data-testid={`onboarding-section-${index}`}>
                    <div className="w-6 h-6 bg-[#002FA7]/10 rounded flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-[#002FA7]">{index + 1}</span>
                    </div>
                    <span className="text-xs sm:text-sm text-[#111827] font-medium">{section}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Training */}
            <div>
              <div className="mb-5 sm:mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-5 h-5 text-[#10B981]" />
                  <p className="text-xs uppercase tracking-[0.15em] text-[#10B981] font-medium">Training</p>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-[#111827] tracking-tight">12 Mandatory Modules</h3>
                <p className="text-sm text-[#4B5563] mt-1">Each module includes content, a quiz (80% to pass), and a PDF certificate.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {trainingModules.map((module, index) => (
                  <div key={index} className="flex items-center gap-2.5 p-2.5 sm:p-3 bg-white rounded-lg border border-black/5" data-testid={`training-module-${index}`}>
                    <div className="w-6 h-6 bg-[#10B981]/10 rounded flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-3 h-3 text-[#10B981]" />
                    </div>
                    <span className="text-xs sm:text-sm text-[#111827] font-medium">{module}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Admin Highlight */}
      <section className="py-14 sm:py-24 bg-[#0A0A0A] text-white" data-testid="admin-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#10B981] font-medium mb-3">For Administrators</p>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-4 sm:mb-6">
                Complete Oversight & Control
              </h3>
              <p className="text-sm sm:text-base text-white/70 mb-6 leading-relaxed">
                Administrators get a powerful dashboard to manage onboarding, scheduling, payroll, compliance, and generate inspection-ready reports — all in one place.
              </p>
              <div className="space-y-3 sm:space-y-4">
                {[
                  { icon: FileSearch, text: "Review & approve each onboarding section with notes" },
                  { icon: Calendar, text: "Assign weekly schedules, manage standby cover, and track confirmations" },
                  { icon: DollarSign, text: "Auto-generated timesheets, invoices, and payroll tracking" },
                  { icon: BarChart3, text: "Tutor reliability scores based on confirmation history" },
                  { icon: AlertTriangle, text: "DBS & Right to Work expiry dashboard with colour-coded alerts" },
                  { icon: Table2, text: "Printable Single Central Record for Ofsted/ISI inspections" }
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <item.icon className="w-4 h-4 text-[#10B981]" />
                    </div>
                    <span className="text-sm text-white/80">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 space-y-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-white/80">Admin Dashboard Preview</span>
                  <div className="flex gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#EF4444]"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-[#10B981]"></div>
                  </div>
                </div>
                {[
                  { label: "Total Staff", val: "24", color: "text-white" },
                  { label: "Fully Onboarded", val: "18", color: "text-[#10B981]" },
                  { label: "This Week's Sessions", val: "42", color: "text-white" },
                  { label: "Pending Confirmations", val: "8", color: "text-[#F59E0B]" },
                  { label: "Monthly Payroll", val: "£6,240", color: "text-[#10B981]" },
                  { label: "Avg Reliability", val: "96%", color: "text-[#10B981]" }
                ].map((row, i) => (
                  <div key={i} className="flex justify-between items-center py-2.5 border-b border-white/5 last:border-0">
                    <span className="text-sm text-white/50">{row.label}</span>
                    <span className={`text-sm font-bold ${row.color}`}>{row.val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-14 sm:py-20 bg-white border-t border-gray-100" data-testid="cta-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-14 h-14 bg-[#0A0A0A] rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Sprout className="w-7 h-7 text-white" />
          </div>
          <h3 className="text-2xl sm:text-3xl font-bold text-[#111827] mb-3 tracking-tight">
            Ready to Get Started?
          </h3>
          <p className="text-sm sm:text-base text-[#4B5563] mb-6 sm:mb-8 max-w-xl mx-auto">
            Register as new staff or log in to continue your onboarding and training journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/login">
              <Button size="lg" className="bg-[#002FA7] hover:bg-[#002482] text-white w-full sm:w-auto text-sm sm:text-base" data-testid="cta-login-button">
                <FileText className="w-4 h-4 mr-2" />
                Staff Login
              </Button>
            </Link>
            <Link to="/register">
              <Button size="lg" variant="outline" className="border-gray-200 text-[#111827] hover:bg-gray-50 w-full sm:w-auto text-sm sm:text-base" data-testid="cta-register-button">
                <UserPlus className="w-4 h-4 mr-2" />
                Register as New Staff
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 bg-[#0A0A0A] text-white border-t border-white/5" data-testid="landing-footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center">
                <Sprout className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-sm sm:text-base tracking-tight">Growing Minds Tuition</span>
            </div>
            <p className="text-xs sm:text-sm text-white/40">
              &copy; 2026 Growing Minds Tuition. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WelcomePage;
